import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

const sizeMap: Record<string, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-3xl',
  xl: 'sm:max-w-5xl',
}

interface ModalProps {
  isOpen: boolean
  toggle: () => void
  size?: string
  className?: string
  children: React.ReactNode
  centered?: boolean
  backdrop?: boolean | 'static'
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  toggle,
  size = 'md',
  className,
  children,
  backdrop,
}) => {
  const handleOpenChange = (open: boolean) => {
    if (!open) toggle()
  }

  return (
    <Dialog open={isOpen} onOpenChange={backdrop === 'static' ? undefined : handleOpenChange}>
      <DialogContent
        className={cn(sizeMap[size], className)}
        onPointerDownOutside={backdrop === 'static' ? (e) => e.preventDefault() : undefined}
      >
        {children}
      </DialogContent>
    </Dialog>
  )
}

interface ModalHeaderProps {
  toggle?: () => void
  className?: string
  children: React.ReactNode
  tag?: React.ElementType
}

export const ModalHeader: React.FC<ModalHeaderProps> = ({ toggle, className, children }) => (
  <DialogHeader className={cn(className)}>
    <DialogTitle>{children}</DialogTitle>
    <DialogDescription className="sr-only">{children}</DialogDescription>
  </DialogHeader>
)

interface ModalBodyProps extends React.HTMLAttributes<HTMLDivElement> {}

export const ModalBody: React.FC<ModalBodyProps> = ({ className, ...props }) => (
  <div className={cn('py-4', className)} {...props} />
)

interface ModalFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

export const ModalFooter: React.FC<ModalFooterProps> = ({ className, ...props }) => (
  <DialogFooter className={cn(className)} {...props} />
)
